/**
 * UserFormFields Component
 * Delade formulärfält för Add/Update User
 * Endast presentation - ingen egen state eller logik
 */

import React from 'react';
import FormInput from '../../../components/common/FormInput';

export const UserFormFields = ({
  values,
  setValues,
  errors,
  builders,
  suppliers,
  previewUrl,
  handleChange,
  handleAvatarChange,
  handleRemoveAvatar,
  isUpdate,
  isAdmin,
  showPasswordFields,
  showEmailOptions,
  onSendWelcomeEmail,
  isSendingEmail
}) => {
  return (
    <>
      {/* Roll */}
      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700">Roll</label>
        <select 
          name="role" 
          value={values.role} 
          onChange={handleChange} 
          required 
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        >
          <option value="">Välj roll</option>
          <option value="admin">Admin</option>
          <option value="byggkonsult">Byggkonsult</option>
          <option value="inredningsarkitekt">Inredningsarkitekt</option>
          <option value="byggare">Byggare</option>
          <option value="UE">Underleverantör</option>
          <option value="projektor">Projektör</option>
          <option value="materialleverantör">Materialleverantör</option>
          <option value="kund">Kund</option>
        </select>
      </div>

      {/* Byggföretag (för byggare) */}
      {values.role === 'byggare' && (
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Byggföretag</label>
          <select 
            name="selectedBuilder" 
            value={values.selectedBuilder} 
            onChange={handleChange} 
            required
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
              errors.selectedBuilder ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Välj byggföretag</option>
            {builders.map(builder => (
              <option key={builder._id} value={builder._id}>{builder.companyName}</option>
            ))}
          </select>
          {errors.selectedBuilder && (
            <p className="text-red-500 text-sm mt-1">{errors.selectedBuilder}</p>
          )}
        </div>
      )}

      {/* Leverantör (för materialleverantör) */}
      {values.role === 'materialleverantör' && (
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Leverantör</label>
          <select 
            name="selectedSupplier" 
            value={values.selectedSupplier} 
            onChange={handleChange} 
            required 
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
              errors.selectedSupplier ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Välj leverantör</option>
            {suppliers.map(supplier => (
              <option key={supplier._id} value={supplier._id}>{supplier.companyName}</option>
            ))}
          </select>
          {errors.selectedSupplier && (
            <p className="text-red-500 text-sm mt-1">{errors.selectedSupplier}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Användaren kopplas till vald leverantör och kan bara hanteras av denna leverantör.
          </p>
        </div>
      )}

      {/* Företagsnamn och UE/Projektör-typ */}
      {(values.role === 'UE' || values.role === 'projektor' || values.role === 'byggkonsult' || values.role === 'inredningsarkitekt' || values.role === 'materialleverantör') && (
        <>
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">Företagsnamn</label>
            <input
              type="text"
              name="companyName"
              value={values.companyName}
              onChange={handleChange}
              placeholder="Ange företagsnamn"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                errors.companyName ? 'border-red-500' : 'border-gray-300'
              }`}
              required={['UE', 'projektor', 'byggkonsult', 'inredningsarkitekt', 'materialleverantör'].includes(values.role)}
              disabled={values.role === 'byggkonsult' || values.role === 'inredningsarkitekt'}
            />
            {errors.companyName && (
              <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>
            )}
          </div>
          
          {values.role === 'UE' && (
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Typ av UE</label>
              <select
                name="contractorType"
                value={values.contractorType}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  errors.contractorType ? 'border-red-500' : 'border-gray-300'
                }`}
                required={values.role === 'UE'}
              >
                <option value="">Välj typ</option>
                <option value="VVS">VVS</option>
                <option value="El">El</option>
                <option value="Bygg">Bygg</option>
                <option value="Plattsättning">Plattsättning</option>
                <option value="Måleri">Måleri</option>
                <option value="Golv">Golv</option>
                <option value="Rivning">Rivning</option>
                <option value="Mark">Mark</option>
                <option value="Övrigt">Övrigt</option>
              </select>
              {errors.contractorType && (
                <p className="text-red-500 text-sm mt-1">{errors.contractorType}</p>
              )}
            </div>
          )}

          {values.role === 'projektor' && (
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Typ av projektör</label>
              <select
                name="projectorType"
                value={values.projectorType}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  errors.projectorType ? 'border-red-500' : 'border-gray-300'
                }`}
                required={values.role === 'projektor'}
              >
                <option value="">Välj typ</option>
                <option value="Konstruktör">Konstruktör</option>
                <option value="Sotare">Sotare</option>
                <option value="Akustiker">Akustiker</option>
                <option value="Ventilationskonsult">Ventilationskonsult</option>
                <option value="VA-konsult">VA-konsult</option>
                <option value="Besiktningsman">Besiktningsman</option>
                <option value="Brandkonsult">Brandkonsult</option>
                <option value="Energikonsult">Energikonsult</option>
                <option value="Geotekniker">Geotekniker</option>
                <option value="Fönster">Fönster</option>
                <option value="Uppmätning bostad">Uppmätning bostad</option>
                <option value="Övrigt">Övrigt</option>
              </select>
              {errors.projectorType && (
                <p className="text-red-500 text-sm mt-1">{errors.projectorType}</p>
              )}

              <label className="flex items-center gap-2 mt-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={values.includeInProjektorList !== false}
                  onChange={(e) => handleChange({ target: { name: 'includeInProjektorList', value: e.target.checked } })}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                Ingår i projektörslistan (kan kontaktas via "Projektörer")
              </label>
            </div>
          )}
        </>
      )}

      {/* Personuppgifter */}
      <FormInput
        label="Förnamn"
        type="text"
        name="firstName"
        value={values.firstName}
        onChange={handleChange}
        placeholder="Ange förnamn"
        required
      />

      <FormInput
        label="Efternamn"
        type="text"
        name="lastName"
        value={values.lastName}
        onChange={handleChange}
        placeholder="Ange efternamn"
        required
      />

      <FormInput
        label="E-post"
        type="email"
        name="email"
        value={values.email}
        onChange={handleChange}
        placeholder="exempel@email.com"
        error={errors.email || ''}
        required
      />

      <FormInput
        label="Telefon"
        type="tel"
        name="phone"
        value={values.phone}
        onChange={handleChange}
        placeholder="073 123 45 67"
        error={errors.phone || ''}
      />

      {/* Lösenordsfält */}
      {showPasswordFields && (
        <>
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              {isUpdate ? 'Nytt lösenord' : 'Lösenord'}
            </label>
            <input
              type="password"
              name="password"
              value={values.password}
              onChange={handleChange}
              placeholder={isUpdate ? 'Lämna tomt för att behålla nuvarande' : 'Ange lösenord'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required={!isUpdate}
            />
            {errors.password && (
              <p className="text-red-500 text-sm mt-1">{errors.password}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Lösenordet måste vara minst 8 tecken och innehålla både bokstäver och siffror.
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="requirePasswordChange"
              name="requirePasswordChange"
              checked={values.requirePasswordChange}
              onChange={(e) => setValues(prev => ({ ...prev, requirePasswordChange: e.target.checked }))}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="requirePasswordChange" className="block text-sm font-medium text-gray-700">
              {isUpdate ? 'Kräv lösenordsändring vid nästa inloggning' : 'Kräv lösenordsändring vid första inloggning'}
            </label>
          </div>

          {/* Kan visa alla projekt — endast relevant för intern personal
              (admin/byggkonsult/inredningsarkitekt), inte externa roller */}
          {isAdmin && ['admin', 'byggkonsult', 'inredningsarkitekt'].includes(values.role) && (
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="canViewAllProjects"
                name="canViewAllProjects"
                checked={values.canViewAllProjects}
                onChange={(e) => setValues(prev => ({ ...prev, canViewAllProjects: e.target.checked }))}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="canViewAllProjects" className="block text-sm font-medium text-gray-700">
                Kan visa alla projekt
              </label>
            </div>
          )}

          {/* Testkund-markering (endast kunder) */}
          {values.role === 'kund' && (
            <div className="space-y-2 pt-2 border-t border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Test</p>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isTestCustomer"
                  name="isTestCustomer"
                  checked={values.isTestCustomer || false}
                  onChange={(e) => setValues(prev => ({ ...prev, isTestCustomer: e.target.checked }))}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="isTestCustomer" className="block text-sm font-medium text-gray-700">
                  Testkund (markera som testdata)
                </label>
              </div>
            </div>
          )}

          {/* Behörigheter för byggkonsulter */}
          {(values.role === 'byggkonsult' || values.role === 'inredningsarkitekt') && (
            <div className="space-y-2 pt-2 border-t border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Extra behörigheter</p>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="hasBookingAccess"
                  name="hasBookingAccess"
                  checked={values.hasBookingAccess || false}
                  onChange={(e) => setValues(prev => ({ ...prev, hasBookingAccess: e.target.checked }))}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="hasBookingAccess" className="block text-sm font-medium text-gray-700">
                  Bokningar (åtkomst till bokningsdashboard)
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="hasAdminAccess"
                  name="hasAdminAccess"
                  checked={values.hasAdminAccess || false}
                  onChange={(e) => setValues(prev => ({ ...prev, hasAdminAccess: e.target.checked }))}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="hasAdminAccess" className="block text-sm font-medium text-gray-700">
                  System (åtkomst till systemdashboard)
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="hasFeedbackAccess"
                  name="hasFeedbackAccess"
                  checked={values.hasFeedbackAccess || false}
                  onChange={(e) => setValues(prev => ({ ...prev, hasFeedbackAccess: e.target.checked }))}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="hasFeedbackAccess" className="block text-sm font-medium text-gray-700">
                  Feedback (åtkomst till feedback-hantering)
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="hasPricingAccess"
                  name="hasPricingAccess"
                  checked={values.hasPricingAccess || false}
                  onChange={(e) => setValues(prev => ({ ...prev, hasPricingAccess: e.target.checked }))}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="hasPricingAccess" className="block text-sm font-medium text-gray-700">
                  Pricing (åtkomst till arbetsbeskrivning-prissättning)
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="hasMarketingAccess"
                  name="hasMarketingAccess"
                  checked={values.hasMarketingAccess || false}
                  onChange={(e) => setValues(prev => ({ ...prev, hasMarketingAccess: e.target.checked }))}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="hasMarketingAccess" className="block text-sm font-medium text-gray-700">
                  Marknad (åtkomst till foto/recensions-utskick)
                </label>
              </div>
              {values.hasMarketingAccess && (
                <div className="ml-6 pl-3 border-l-2 border-gray-200 space-y-2">
                  <p className="text-xs text-gray-500">Välj vilka flikar användaren ska se inom Marknad:</p>
                  {[
                    { key: 'contentPlan',   label: 'Content-plan' },
                    { key: 'ideer',         label: 'Idéer' },
                    { key: 'fotografering', label: 'Fotografering' }
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`marketingTab_${key}`}
                        checked={values.marketingTabAccess?.[key] || false}
                        onChange={(e) => setValues(prev => ({
                          ...prev,
                          marketingTabAccess: {
                            ...(prev.marketingTabAccess || {}),
                            [key]: e.target.checked
                          }
                        }))}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`marketingTab_${key}`} className="block text-sm text-gray-700">
                        {label}
                      </label>
                    </div>
                  ))}
                </div>
              )}

              {/* Ligger EFTER Marknads undergrupp. Placerad före den såg de
                  indragna flikarna ut att höra till Projektörer i stället. */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="canEditProjektorer"
                  name="canEditProjektorer"
                  checked={values.canEditProjektorer || false}
                  onChange={(e) => setValues(prev => ({ ...prev, canEditProjektorer: e.target.checked }))}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="canEditProjektorer" className="block text-sm font-medium text-gray-700">
                  Projektörer (får lägga till och ändra i mail-listan)
                </label>
              </div>
            </div>
          )}

          {/* Behörigheter för byggare */}
          {values.role === 'byggare' && (
            <div className="space-y-2 pt-2 border-t border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Extra behörigheter</p>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="canCompleteColleaguesActions"
                  name="canCompleteColleaguesActions"
                  checked={values.canCompleteColleaguesActions || false}
                  onChange={(e) => setValues(prev => ({ ...prev, canCompleteColleaguesActions: e.target.checked }))}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="canCompleteColleaguesActions" className="block text-sm font-medium text-gray-700">
                  Tillåt avbockning av kollegors Att göra-punkter
                </label>
              </div>
            </div>
          )}

          {/* Skicka välkomstmail (UpdateUser) */}
          {isUpdate && isAdmin && onSendWelcomeEmail && (
            <div className="pt-2">
              <button
                type="button"
                onClick={onSendWelcomeEmail}
                disabled={isSendingEmail || !values.password}
                className={`mt-2 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  isSendingEmail || !values.password ? 'bg-leadme-pink-light cursor-not-allowed' : 'bg-leadme-pink hover:bg-leadme-pink-dark'
                }`}
              >
                {isSendingEmail ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Skickar...
                  </>
                ) : (
                  <>
                    <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Skicka välkomstmail
                  </>
                )}
              </button>
              {!values.password && (
                <p className="text-xs text-amber-600 mt-1">
                  Du måste ange ett lösenord för att kunna skicka välkomstmail
                </p>
              )}
            </div>
          )}
        </>
      )}

      {/* Skicka välkomstmail checkbox (AddUser) */}
      {showEmailOptions && (
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="sendWelcomeEmail"
            name="sendWelcomeEmail"
            checked={values.sendWelcomeEmail}
            onChange={(e) => setValues(prev => ({ ...prev, sendWelcomeEmail: e.target.checked }))}
            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          />
          <label htmlFor="sendWelcomeEmail" className="block text-sm font-medium text-gray-700">
            Skicka välkomstmail till användaren med inloggningsuppgifter
          </label>
        </div>
      )}

      {/* Avatar upload */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Profilbild
        </label>
        <div className="flex items-center space-x-4">
          <div className="relative w-16 h-16">
            {previewUrl && (
              <>
                <img
                  src={previewUrl}
                  alt="Avatar preview"
                  className="w-16 h-16 rounded-full object-cover"
                />
                <button
                  type="button"
                  onClick={handleRemoveAvatar}
                  className="absolute -top-2 -right-2 bg-leadme-red text-white rounded-full p-1 hover:bg-leadme-red-dark"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </>
            )}
          </div>
          <div>
            <input
              type="file"
              accept="image/*"
              id="avatar-upload"
              onChange={e => {
                const file = e.target.files[0];
                if (file) handleAvatarChange(file);
              }}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-leadme-pink-light file:text-white hover:file:bg-leadme-pink"
              aria-label="Ladda upp ny profilbild"
              tabIndex={0}
            />
          </div>
        </div>
        {errors.avatar && (
          <p className="text-red-500 text-sm mt-1">{errors.avatar}</p>
        )}
      </div>
    </>
  );
};

